import { useState } from "react";
import { authService } from "../services/auth";

interface OneTapSignInProps {
  onSuccess?: (user: any) => void;
  onError?: (error: string) => void;
  className?: string;
}

export function OneTapSignIn({
  onSuccess,
  onError,
  className = "",
}: OneTapSignInProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleOneTapSignIn = async () => {
    try {
      setIsLoading(true);

      // For now, redirect to Google OAuth as a fallback
      // This can be enhanced later when One-Tap plugin is available
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

      if (!googleClientId) {
        onError?.("Google Client ID not configured");
        return;
      }

      // Redirect to Google OAuth
      const redirectUri = encodeURIComponent(
        `${window.location.origin}/auth/callback`
      );
      const scope = encodeURIComponent("openid email profile");
      const state = encodeURIComponent(
        JSON.stringify({
          redirectTo: window.location.pathname,
          isOneTap: true,
        })
      );

      const googleAuthUrl =
        `https://accounts.google.com/oauth/authorize?` +
        `client_id=${googleClientId}&` +
        `redirect_uri=${redirectUri}&` +
        `scope=${scope}&` +
        `state=${state}&` +
        `response_type=code&` +
        `access_type=offline&` +
        `prompt=consent`;

      window.location.href = googleAuthUrl;
    } catch (error) {
      console.error("One-Tap sign-in error:", error);
      onError?.("Failed to initiate One-Tap sign-in");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`one-tap-signin ${className}`}>
      <button
        onClick={handleOneTapSignIn}
        disabled={isLoading}
        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
        {isLoading ? (
          <div className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 animate-spin"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Signing in...
          </div>
        ) : (
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google One Tap
          </div>
        )}
      </button>

      <p className="mt-2 text-xs text-gray-500 text-center">
        One-Tap sign-in with Google OAuth
      </p>
    </div>
  );
}
